import React from 'react';
import type { HighlightCampaign } from '../../types';
import '../../styles/highlight/HighlightLedger.css';

interface HighlightLedgerProps {
  data: HighlightCampaign;
  now?: Date;
}

interface MeterStyle extends React.CSSProperties {
  '--psl-hl-pct'?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Dot thousands separator, always — pt-PT's locale skips grouping under
// 10.000 and uses a thin space above it, which reads inconsistently on paper.
function grouped(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function euros(n: number): string {
  return `${grouped(n)}€`;
}

function shortDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/**
 * The clinic "conta corrente" — a torn receipt slip showing how much of the
 * vet debt the crowdfunding has covered. Sits on the right page in place of
 * the helpNote when a highlight carries `campaign` data.
 */
const HighlightLedger: React.FC<HighlightLedgerProps> = ({ data, now = new Date() }) => {
  const pct = Math.min(100, Math.max(0, (data.raised / data.goal) * 100));
  const shownPct = pct > 0 && pct < 1 ? '<1' : String(Math.round(pct));
  const daysLeft = Math.max(0, Math.ceil((new Date(data.endsAt).getTime() - now.getTime()) / DAY_MS));
  const meterStyle: MeterStyle = { '--psl-hl-pct': `${pct}%` };

  return (
    <div className="psl-hl-ledger">
      <div className="psl-hl-ledger__head">
        <span className="psl-hl-ledger__lbl">{data.label}</span>
        <span className="psl-hl-ledger__ref">ppl · {shortDate(data.updatedAt)}</span>
      </div>

      <div className="psl-hl-ledger__sum">
        <span className="psl-hl-ledger__raised">{euros(data.raised)}</span>
        <span className="psl-hl-ledger__goal">
          de <em>{euros(data.goal)}</em>
        </span>
      </div>

      <div
        className="psl-hl-ledger__meter"
        style={meterStyle}
        role="progressbar"
        aria-label="Angariado até agora"
        aria-valuemin={0}
        aria-valuemax={data.goal}
        aria-valuenow={data.raised}
        aria-valuetext={`${euros(data.raised)} de ${euros(data.goal)}`}
      >
        <span className="psl-hl-ledger__fill" />
        <span className="psl-hl-ledger__paw" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <ellipse cx="12" cy="15.5" rx="4.6" ry="4" />
            <circle cx="6" cy="10" r="2" />
            <circle cx="9.6" cy="6.2" r="2" />
            <circle cx="14.4" cy="6.2" r="2" />
            <circle cx="18" cy="10" r="2" />
          </svg>
        </span>
        <span className="psl-hl-ledger__ticks" aria-hidden="true" />
      </div>

      <dl className="psl-hl-ledger__facts">
        <div>
          <dt>pago</dt>
          <dd>{shownPct}%</dd>
        </div>
        <div>
          <dt>apoiantes</dt>
          <dd>{data.backers}</dd>
        </div>
        <div>
          <dt>{daysLeft === 1 ? 'falta' : 'faltam'}</dt>
          <dd>
            {daysLeft} <small>{daysLeft === 1 ? 'dia' : 'dias'}</small>
          </dd>
        </div>
      </dl>

      {data.tiers && data.tiers.length > 0 && (
        <div className="psl-hl-ledger__tiers">
          <span className="psl-hl-ledger__tiers-lbl">chegamos lá com</span>
          {data.tiers.map((t) => (
            <span key={t.amount} className="psl-hl-ledger__tier">
              <b>{t.amount}€</b> × {grouped(t.people)}
            </span>
          ))}
        </div>
      )}

      {data.handTag && <span className="psl-hl-ledger__hand">{data.handTag}</span>}
    </div>
  );
};

export default HighlightLedger;
